import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import { applyInterpolation } from 'automation-ui-action-features/actions/extension/utils/interpolation-utils';
import sanitizeWithExternalLinks from 'automation-ui-action-features/actions/extension/utils/sanitizeWithExternalLinks';
import { FieldKeys } from 'automation-ui-log-events/constants/LogEventMessageConstants';
import TranslatedLogEventMessageRecord from 'automation-ui-log-events/records/LogEventMessage/TranslatedLogEventMessage';
import { getLogEventDetail } from 'automation-ui-log-events/utils/logEventUtils';
import EditPropertyLink from 'automation-ui-references/object-type-properties/EditPropertyLink';
import DocButton from 'automation-ui-usage-tracking/components/DocButton';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const NormalWeightSpan = styled.span.withConfig({
  displayName: "LegacyLogEventMessage__NormalWeightSpan",
  componentId: "p2j1we-0"
})(["font-weight:normal;"]);
const InheritedWeightSpan = styled.span.withConfig({
  displayName: "LegacyLogEventMessage__InheritedWeightSpan",
  componentId: "p2j1we-1"
})(["font-weight:inherit;"]);

/**
 * Used for external links where we don't want to break the flow of the content.
 * For example, if a link has a trailing punctuation.
 *   Fix this by {updating the setting}. {Learn more}[↗]
 */
const NoIconExternalLink = ({
  children,
  href
}) => /*#__PURE__*/_jsx("a", {
  href: href,
  rel: "noreferrer",
  target: "_blank",
  children: children
});
const getFieldLabel = message => {
  const {
    fieldKey
  } = message;
  return fieldKey !== FieldKeys.DEFAULT ? /*#__PURE__*/_jsxs("strong", {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: `automation-ui-log-events.messageFields.${fieldKey}`
    }), ': ']
  }) : null;
};
const getMessage = (message, messageOptions) => {
  const {
    fieldKey
  } = message;
  const MessageSpan = fieldKey === FieldKeys.DEFAULT ? InheritedWeightSpan : NormalWeightSpan;
  if (message instanceof TranslatedLogEventMessageRecord) {
    return /*#__PURE__*/_jsx(MessageSpan, {
      children: sanitizeWithExternalLinks(applyInterpolation(message.getMessage() || '', {}))
    });
  }
  const {
    path,
    isJSXMessage
  } = message;
  if (path) {
    const FormattedLogEventMessage = isJSXMessage ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: path,
      options: messageOptions,
      elements: {
        Link: UILink,
        NoIconExternalLink,
        PropertyLink: EditPropertyLink,
        DocButton
      }
    }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: path,
      options: messageOptions
    });
    return /*#__PURE__*/_jsx(MessageSpan, {
      children: FormattedLogEventMessage
    });
  } else {
    return null;
  }
};
const LegacyLogEventMessage = ({
  logEvent,
  flowObjectTypeId,
  shouldTruncate = false
}) => {
  const ListItemWrap = ({
    children
  }) => shouldTruncate ? /*#__PURE__*/_jsx(TruncateString, {
    children: children
  }) : /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
  // *** NOTE ***
  // `getLogEventDetail` needs to be called *after* lang files
  // are loaded, as the key path is dynamically generated based on
  // which keys are available. Leaving this note for when/if
  // `LazyLangLoaderDecorator` is used within this component.

  const {
    messages,
    messageOptions
  } = getLogEventDetail(logEvent, flowObjectTypeId);
  const messageListItems = messages.map((message, index) => {
    const FieldLabel = getFieldLabel(message);
    const Message = getMessage(message, messageOptions);
    return /*#__PURE__*/_jsx("span", {
      children: /*#__PURE__*/_jsxs(ListItemWrap, {
        children: [FieldLabel, Message]
      })
    }, index);
  });
  return /*#__PURE__*/_jsx(UIList, {
    children: messageListItems
  });
};
export default LegacyLogEventMessage;